/**
 * Analytics Events Queue
 *
 * A generic queue for analytics events that can be consumed by multiple
 * analytics providers (Google Analytics, Vitally, etc.).
 *
 * Events are pushed to the queue from server-side via flash[:analytics_events]
 * or client-side via Slzr.AnalyticsQueue.push().
 *
 * Analytics providers subscribe to the queue to receive events.
 *
 * The server-side AnalyticsEventsQueueHelper may render an inline script that
 * creates Slzr.AnalyticsQueue with pre-queued events before Webpack loads.
 * initAnalyticsQueue preserves that existing queue (via ||=) so those events
 * are replayed to late subscribers.
 */

/**
 * Initialize the Analytics Event Queue on the Slzr global object.
 *
 * Sets up Slzr.AnalyticsQueue with subscribe, push, and pushFromResponse
 * methods. Preserves any pre-existing queue (from server-side rendering).
 *
 * Must be called before any analytics provider's init function
 * (initGoogleAnalytics, initVitallyAnalytics).
 *
 * @param {Object} Slzr - The Slzr global object
 */
export const initAnalyticsQueue = (Slzr) => {
  Slzr.AnalyticsQueue = Slzr.AnalyticsQueue || {
    events: [],
    _subscribers: [],
    _initialized: false,
    _providers: {
      google: 'google-analytics',
      vitally: 'vitally-analytics',
    }
  };

  /**
   * Subscribe to receive analytics events.
   * The callback will be called for each event pushed to the queue,
   * including any events that were queued before subscribing.
   *
   * @param {function} callback - Function to call with each event
   */
  Slzr.AnalyticsQueue.subscribe = (callback) => {
    if (typeof callback !== 'function') return;

    Slzr.AnalyticsQueue._subscribers.push(callback);

    // Process any events that were queued before this subscriber registered
    if (Slzr.AnalyticsQueue.events?.length > 0) {
      Slzr.AnalyticsQueue.events.forEach((event) => {
        try {
          callback(event);
        } catch (err) {
          console.error('[AnalyticsQueue] Error in subscriber:', err);
        }
      });
    }
  };

  /**
   * Push an event to the queue.
   * All subscribers will be notified of the event.
   *
   * @param {Object} event - Event object with category, action, label, value
   */
  Slzr.AnalyticsQueue.push = (event) => {
    // Store the event
    Slzr.AnalyticsQueue.events = Slzr.AnalyticsQueue.events || [];
    Slzr.AnalyticsQueue.events.push(event);

    // Notify all subscribers
    if (Slzr.AnalyticsQueue._subscribers?.length > 0) {
      Slzr.AnalyticsQueue._subscribers.forEach((callback) => {
        try {
          callback(event);
        } catch (err) {
          console.error('[AnalyticsQueue] Error in subscriber:', err);
        }
      });
    }
  };

  /**
   * Convenience method for handling server responses that include analytics_events.
   *
   * @param {Object} response - Server response object with optional analytics_events array
   */
  Slzr.AnalyticsQueue.pushFromResponse = (response) => {
    if (response?.analytics_events?.length > 0) {
      response.analytics_events.forEach((event) => {
        Slzr.AnalyticsQueue.push(event);
      });
    }
  };
};
